@import '../../stylesheets/mixins/common.styl'
@import 'common/stylesheets/mixins/marketing-site'

$animation-class = '.mp-button-animation-layer'
// The button's own icons and spinner. Caller content sits in `.mp-button-content-text` and keeps its
// own icon styles.
$own-icon = 'svg-icon:not(.mp-button-content-text svg-icon)'
$animation-trigger = '&:hover:not(.is-disabled), &:active:not(.is-disabled), &.is-pressed:not(.is-disabled)'

$border-width = 1px
$opacity1 = 0.06
$opacity2 = 0.12

content-color(color)
  +content-styles()
    color color

/* Theming mixins */
primary-styles(default-color, hover-color, active-color)
  background-color default-color
  expand-animation(hover-color, $animation-class, $animation-trigger, 1, '%s' % var(--button-animation-scale, 0.35))

  &:active{$not-disabled}, &.is-pressed{$not-disabled}
    {$animation-class}
      background-color active-color

common-button-styles(hover-bg-color, hover-text-color, active-bg-color, active-text-color, highlighted-icon-color, $duration-factor = 1)
  &:not(.disable-pressed-styles)
    expand-animation(hover-bg-color, $animation-class, $animation-trigger, $duration-factor, '%s' % var(--button-animation-scale, 0.35))

    {$animation-trigger}
      content-color(hover-text-color)

    &:active{$not-disabled}, &.is-pressed{$not-disabled}
      content-color(active-text-color)

      {$animation-class}
        background-color active-bg-color

  {$own-icon}.highlighted
    color highlighted-icon-color

secondary-styles(color-rgb, color-hex)
  common-button-styles('rgba(%s, %s)' % (color-rgb $opacity1), color-hex, 'rgba(%s, %s)' % (color-rgb $opacity2), color-hex, color-hex)

  {$animation-trigger}
    border-color 'rgba(%s, %s)' % (color-rgb $opacity2)

  {$animation-class}
    border $border-width solid transparent // workaround to completely fill the button with a border
    box-sizing initial

tertiary-styles(color-rgb, color-hex)
  common-button-styles('rgba(%s, %s)' % (color-rgb $opacity1), color-hex, 'rgba(%s, %s)' % (color-rgb $opacity2), color-hex, color-hex, 0.5)

/* End section */

// default secondary theme, medium size
.mp-button-root
  --button-animation-scale 0.35
  --button-content-padding 0
  align-items center
  border-radius var(--button-border-radius, $border-radius-medium)
  cursor pointer
  display inline-flex
  font-family $family-helvetica
  font-size $font-size-medium
  font-weight var(--button-font-weight, $weight-medium)
  height 32px
  outline 0
  position relative
  white-space nowrap
  width var(--button-root-width, auto)
  content-color(var(--primary-text-color))

.mp-button-root[data-button-disabled]
  cursor not-allowed

.mp-button-root[data-button-readonly]
  cursor default

  .mp-button-container
    pointer-events none

// The board grid-row drag control is an <SelectEl icon={{leftIcon: 'gripper'}}>,
// and select/template.tsx forwards leftIcon as the inner mp-button's icon attr,
// so the gripper cursor has to live here rather than at that callsite.
.mp-button-root[data-button-icon="gripper"]:not([data-button-pressed])
  cursor grab

.mp-button-root[data-button-icon="gripper"]:active
  cursor grabbing

.mp-button-root[data-button-size="large"]
  border-radius $border-radius-large
  font-size $font-size-large
  height 40px

a.mp-button-container.mp-button-browse
  font-size $font-size-medium

.mp-button-root[data-button-size="small"]
  --button-icon-size 18px
  border-radius $border-radius-small
  font-size $font-size-small
  height 24px

  .mp-button-container
    padding var(--button-padding, 8px)

.mp-button-root[data-button-size="small-icon"]
  --button-icon-size 18px
  border-radius $border-radius-small
  font-size $font-size-small
  height 22px
  width 22px

.mp-button-root[data-button-size="tiny"]
  border-radius $border-radius-small
  font-size $font-size-small
  height 12px
  width 12px

.mp-button-root[data-button-size="inline"]
  border-radius $border-radius-small
  font-size $font-size-small
  height 24px

  .mp-button-container
    padding var(--button-padding, 8px 2px)

.mp-button-root[data-button-theme="primary"]:focus
  mp-focus-box-shadow()

.mp-button-root[data-button-theme="upgrade"]:focus
  background $mustard200
  color $mustard200
  mp-2023-focus-box-shadow($mustard200)

.mp-button-root[data-button-theme="select"]:focus
  mp-focus-box-shadow()

.mp-button-root[data-button-theme="menu-footer"]:focus
  mp-focus-box-shadow()

.mp-button-root[data-button-theme="menu-filter"]:focus
  mp-focus-box-shadow()

.mp-button-root[data-button-theme="query-builder"]:focus:not(:active):not(:hover)
  .mp-button-container
    background-color 'rgba(%s, 0.06)' % var(--blue1-rgb)

.mp-button-root:focus:not(:active):not(:hover)
  .mp-button-container
    &.mp-button-primary
      mp-focus-box-shadow()

    &.mp-button-marketing-primary
      background $white
      border 2px solid $purple40
      color $purple140
      mp-2023-focus-box-shadow()

    &.mp-button-marketing-secondary
      background $base100
      border 2px solid $purple140
      color $purple140
      mp-2023-focus-box-shadow($purple140)

    &.mp-button-secondary, &.mp-button-tertiary
      &:not(.is-pressed):not(.disable-pressed-styles)
        background-color 'rgba(%s, 0.06)' % var(--blue1-rgb)

        &.type-success
          background-color 'rgba(%s, 0.06)' % var(--green1-rgb)

        &.type-info
          background-color 'rgba(%s, 0.06)' % var(--blue1-rgb)

        &.type-warn
          background-color 'rgba(%s, 0.06)' % var(--orange1-rgb)

        &.type-destructive
          background-color 'rgba(%s, 0.06)' % var(--red1-rgb)

    &.mp-button-lightweight
      background-color transparent

.mp-button-root[data-button-theme="primary"]
  content-color($white)

.mp-button-root[data-button-theme="marketing-primary"]
  btn-font()
  content-color($white)

  .mp-button-container
    padding-left 32px
    padding-right 32px

.mp-button-root[data-button-theme="marketing-secondary"]
  btn-font()
  content-color($purple100)

  .mp-button-container
    padding-left 32px
    padding-right 32px

.mp-button-root[data-button-theme="upgrade"]
  content-color($black)

.mp-button-root[data-button-theme="spark"]
  content-color($white)

.mp-button-root[data-button-theme="chip"]
  --button-icon-size 14px
  border-radius 4px
  height 22px
  max-width 220px
  padding 0
  vertical-align bottom
  width fit-content

  .mp-button-content-text
    display inline-block

.mp-button-root[data-button-theme="menu-footer"]
  border-radius 0px 0px 12px 12px
  border-top $border-width solid var(--component-outline-color)

.mp-button-root[data-button-layout="vertical"]
  height auto

.mp-button-root.truncated
  .mp-button-container
    .mp-button-content
      overflow hidden

    .mp-button-content-text
      line-height 1.4
      truncate-text()

a.mp-button-container
  align-items inherit
  border-radius inherit
  box-sizing border-box
  color inherit
  cursor inherit
  display inline-flex
  font-family inherit
  font-size inherit
  font-weight inherit
  height 100%
  justify-content center
  letter-spacing 0
  min-height inherit
  outline 0
  overflow hidden
  padding 0 var(--button-padding, 12px)
  position relative
  text-decoration none
  transition color $color-speed ease, background-color $color-speed ease
  user-select none
  width 100%

  // The page's own `a:hover` and `a:visited` colors tie with `color inherit` above and load after
  // this stylesheet. Listed before the themes so their hover colors still win.
  &:hover, &:visited
    color inherit

  {$disabled}
    opacity 0.4

  &.disabled-stays-opaque
    {$disabled}
      opacity 1

  .mp-button-content
    align-items center
    display flex
    flex 1
    flex-direction var(--button-flex-direction, row)
    gap var(--button-content-gap, 0)
    justify-content var(--button-justify-content, center)
    position relative
    width 100%
    {$own-icon}
      svg-icon-style-props(size: '%s' % var(--button-icon-size, 22px), base-color: '%s' % var(--button-icon-color, inherit))
      margin-left var(--button-icon-margin-left, 0px)
      margin-right var(--button-icon-margin-right, 8px)

      &[icon='plus']
        margin-left var(--button-icon-margin-left, -4px)
        margin-right var(--button-icon-margin-right, 4px)

      &[icon='verified-filled'], &[icon='verified-outlined']
        margin-right 0px

    {$own-icon}.right
      margin-left var(--button-icon-margin-left, 8px)
      margin-right var(--button-icon-margin-right, 0)

      &[icon='caret-down']
        margin-left -2px
        margin-right -2px

    {$own-icon}.highlighted
      color var(--blue1)

    {$own-icon}.filled
      svg-icon-color-props(fill: '%s' % var(--button-icon-fill, none), fill-opacity: '%s' % var(--button-icon-fill-opacity, 1))

  .mp-button-content-text
    align-items center
    display var(--button-content-display, inline-flex)
    width var(--button-content-width, auto)

  &.is-icon-only
    padding 0 var(--button-padding, 5px)

    {$own-icon}
      margin 0

      &[icon='plus']
        margin-left 0
        margin-right 0

  .mp-button-animation-layer
    border-radius inherit
    cursor inherit

  /* BEGIN COLOR THEMES */
  &.mp-button-primary
    &{$disabled}
      background-color var(--gray1)
      content-color(var(--primary-text-color))

    primary-styles(var(--primary-default, var(--blue1)), var(--primary-hover, var(--blue2)), var(--primary-active, var(--blue3)))

    &.type-success
      primary-styles('%s' % var(--green1), '%s' % var(--green2), '%s' % var(--green3))

      &{$disabled}
        content-color($white)

    &.type-warn
      primary-styles('%s' % var(--orange1), '%s' % var(--orange2), '%s' % var(--orange3))

      &{$disabled}
        content-color($white)

    &.type-info
      primary-styles(var(--primary-default, var(--blue1)), var(--primary-hover, var(--blue2)), var(--primary-active, var(--blue3)))

      &{$disabled}
        content-color($white)

    &.type-destructive
      primary-styles('%s' % var(--red1), '%s' % var(--red2), '%s' % var(--red3))

      &{$disabled}
        content-color($white)

  &.mp-button-secondary, &.mp-button-templates-event
    /* this color is somewhat semantically wrong while we figure out our color system */
    background-color var(--background-color)
    border $border-width solid var(--component-outline-color)
    secondary-styles(var(--blue1-rgb), var(--blue1))

    &.type-success
      secondary-styles(var(--green1-rgb), var(--green1))

    &.type-info
      secondary-styles(var(--blue1-rgb), var(--blue1))

    &.type-warn
      secondary-styles(var(--orange1-rgb), var(--orange1))

    &.type-destructive
      secondary-styles(var(--red1-rgb), var(--red1))

  &.mp-button-chip
    /* this color is somewhat semantically wrong while we figure out our color system */
    background-color var(--gray1)
    border $border-width solid var(--gray2)
    secondary-styles(var(--blue1-rgb), var(--blue1))

  &.mp-button-tertiary, &.mp-button-query-builder, &.mp-button-qb-subitem, &.mp-button-menu-footer
    tertiary-styles(var(--blue1-rgb), var(--blue1))

    &.type-success
      tertiary-styles(var(--green1-rgb), var(--green1))

    &.type-info
      tertiary-styles(var(--blue1-rgb), var(--blue1))

    &.type-warn
      tertiary-styles(var(--orange1-rgb), var(--orange1))

    &.type-destructive
      tertiary-styles(var(--red1-rgb), var(--red1))

  &.mp-button-lightweight
    content-color(var(--tertiary-text-color))

    {$animation-trigger}
      &.type-success
        content-color(var(--green1))

      &.type-info
        content-color(var(--blue1))

      &.type-warn
        content-color(var(--orange1))

      &.type-destructive
        content-color(var(--red1))

  &.mp-button-select, &.mp-button-browse
    background-color var(--secondary-background-color)

    {$animation-trigger}
      border-color var(--blue1)

    common-button-styles('%s' % var(--secondary-background-color), '%s' % var(--blue1), 'rgba(%s, 0.08)' % var(--blue1-rgb), '%s' % var(--blue1), '%s' % var(--blue1))

  &.mp-button-select
    border $border-width solid var(--secondary-background-color)

  &.mp-button-query-builder
    .mp-button-content
      justify-content flex-start
      padding-left var(--button-content-padding)
      padding-right var(--button-content-padding)

  &.mp-button-qb-subitem
    color var(--button-text-color, var(--secondary-text-color))

  &.mp-button-layout-vertical
    .mp-button-content
      flex-direction column

    .mp-button-content-text
      color var(--gray4)
      font-size $font-size-small

  &.mp-button-templates-event
    padding-bottom 20px
    padding-top 20px

    .mp-button-content
      color var(--gray4)
      justify-content flex-start

&.mp-button-marketing-primary
  &{$disabled}
    background-color $gray10
    content-color($gray80)

  primary-styles($purple100, $purple140, $purple40)

  &.type-info
    primary-styles($purple100, $purple140, $purple40)

    &:hover
      mp-2023-hover-box-shadow()

    &{$disabled}
      content-color($white)
      opacity 0.5

&.mp-button-marketing-secondary
  background-color $base100
  border 2px solid $purple100
  common-button-styles($base100, $purple100, $base100, $purple40, $purple140, $duration-factor = 1)

  &{$disabled}
    content-color($purple100)
    opacity 0.5

  &.type-info
    common-button-styles($base100, $purple100, $base100, $purple40, $purple140, $duration-factor = 1)

    {$animation-trigger}
      content-color($purple140)

    &:active{$not-disabled}, &.is-pressed{$not-disabled}
      border 2px solid $purple40
      content-color($purple40)

      {$animation-class}
        background-color $base100

    &:hover
      border 2px solid $purple140
      content-color($purple140)

&.mp-button-upgrade
  background-color $mustard100

  &:hover
    background-color #EBA712

  &:active{$not-disabled}, &.is-pressed{$not-disabled}
    background-color #DE9800

&.mp-button-spark
  background var(--spark-gradient-color)
